import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import * as Sentry from '@sentry/react';
import { setCompanyHasSecurityAddon } from '../../../../api/firebase/function/security/securityAddonFunction';
import { SECURITY_ADDON_TOGGLE_ERROR } from '../../../../constants/labels';
import {
  gray40,
  primaryButtonBackgroundColor,
  red20,
  red50,
  tertiaryButtonBackgroundColor,
} from '../../../../constants/colors';
import { dialogText, dialogTitle } from '../../../../constants/location';

interface Props {
  open: boolean;
  companyId: string;
  /** 変更前の有効状態。ダイアログはこの反対の状態へ切り替える前提で開く。 */
  currentEnabled: boolean;
  onClose: () => void;
  /** 切替成功時に新しい有効状態を返す。 */
  onChanged: (after: boolean) => void;
}

const styles = {
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      width: '480px',
      '@media screen and (max-width:960px)': {
        padding: '32px 16px',
      },
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  dialogTitle,
  fieldWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  bodyText: {
    ...dialogText,
  },
  errorBox: {
    backgroundColor: red20,
    border: `1px solid ${red50}`,
    borderRadius: '4px',
    color: red50,
    fontSize: '14px',
    padding: '8px 12px',
  },
  dialogActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '32px',
  },
  actionButton: {
    height: '44px',
    minWidth: '120px',
  },
};

/**
 * 運営スタッフ用: 企業ごとのセキュリティアドオン（has_security_addon）オン/オフ確認ダイアログ（#2982-2985）。
 * Webhook / API 連携機能トグルと同じく、企業詳細「概要」タブの行から開く。
 * 有効化するとその企業で IP制限 / SAML / MFA強制 / 監査ログ が利用可能になる。
 */
const SecurityAddonToggleDialog: React.FC<Props> = ({
  open,
  companyId,
  currentEnabled,
  onClose,
  onChanged,
}) => {
  const pendingState = !currentEnabled;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setSubmitting(false);
      setError('');
    }
  }, [open]);

  const handleClose = (): void => {
    if (submitting) return;
    onClose();
  };

  const handleConfirm = async (): Promise<void> => {
    setSubmitting(true);
    setError('');
    try {
      const response = await setCompanyHasSecurityAddon({
        company_id: companyId,
        enabled: pendingState,
      });
      onChanged(response.after);
      onClose();
    } catch (e) {
      Sentry.captureException(e);
      setError(e instanceof Error ? e.message : SECURITY_ADDON_TOGGLE_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} sx={styles.dialog}>
      <Box sx={styles.dialogHeader}>
        <Typography sx={styles.dialogTitle}>
          セキュリティアドオンを{pendingState ? '有効化' : '無効化'}しますか？
        </Typography>
        <IconButton onClick={handleClose} disabled={submitting} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={styles.fieldWrapper}>
        {error && <Box sx={styles.errorBox}>{error}</Box>}

        <Typography sx={styles.bodyText}>
          {pendingState
            ? 'この企業のセキュリティアドオンを有効化します。IP制限 / SAML SSO / MFA 強制 / 監査ログ の設定が可能になります。'
            : '無効化すると、この企業のセキュリティ設定タブは非表示になり、IP制限 / SAML / MFA 強制 / 監査ログ が利用できなくなります。'}
        </Typography>

        <Box sx={styles.dialogActions}>
          <Button
            onClick={handleClose}
            disabled={submitting}
            sx={[tertiaryButtonBackgroundColor, styles.actionButton]}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={[primaryButtonBackgroundColor, styles.actionButton]}
          >
            {pendingState ? '有効化する' : '無効化する'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default SecurityAddonToggleDialog;
